import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { edgeFnUrl } from '@saas-boilerplate/shared';

type Prefs = {
  email_unsubscribed_all: boolean;
  email_type_optouts: string[];
  email_series_optouts: string[];
};

const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

async function callFn(body: Record<string, unknown>) {
  const res = await fetch(edgeFnUrl('notification-preferences'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(ANON_KEY ? { apikey: ANON_KEY, Authorization: `Bearer ${ANON_KEY}` } : {}),
    },
    body: JSON.stringify(body),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
  return json;
}

export function UnsubscribePage() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') || '';
  const seriesId = params.get('series') || null;
  const action = (params.get('action') || 'manage').toLowerCase();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState<string>('');
  const [workspaceName, setWorkspaceName] = useState<string>('Workspace');
  const [prefs, setPrefs] = useState<Prefs>({
    email_unsubscribed_all: false,
    email_type_optouts: [],
    email_series_optouts: [],
  });
  const [saving, setSaving] = useState(false);
  const [savedNotice, setSavedNotice] = useState<string | null>(null);
  const [oneClickHandled, setOneClickHandled] = useState(false);

  useEffect(() => {
    (async () => {
      if (!token) {
        setError('This link is missing its token.');
        setLoading(false);
        return;
      }
      try {
        const res = await callFn({ action: 'lookup', token });
        setEmail(res.email);
        setWorkspaceName(res.workspace_name);
        setPrefs(res.prefs);
      } catch (e: any) {
        setError(e.message || 'Failed to load preferences.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  // Handle one-click unsubscribe link (action=unsubscribe in URL) - no extra confirmation needed
  useEffect(() => {
    if (oneClickHandled) return;
    if (loading || error || !token) return;
    if (action !== 'unsubscribe') return;
    setOneClickHandled(true);
    (async () => {
      setSaving(true);
      try {
        const res = await callFn({ action: 'unsubscribe', token, series_id: seriesId });
        if (res.series_opted_out) {
          setSavedNotice(`Unsubscribed from this meeting series. Other notifications will still arrive.`);
          setPrefs((p) => ({
            ...p,
            email_series_optouts: [...new Set([...(p.email_series_optouts || []), seriesId!])],
          }));
        } else {
          setSavedNotice(`Unsubscribed from all upstand.ai emails for ${email}.`);
          setPrefs((p) => ({ ...p, email_unsubscribed_all: true }));
        }
      } catch (e: any) {
        setError(e.message || 'Failed to unsubscribe.');
      } finally {
        setSaving(false);
      }
    })();
  }, [oneClickHandled, loading, error, token, action, seriesId, email]);

  const toggleType = (type: 'recap' | 'pre_nudge' | 'morning_briefing', on: boolean) => {
    setPrefs((p) => {
      const s = new Set(p.email_type_optouts);
      if (on) s.delete(type);
      else s.add(type);
      return { ...p, email_type_optouts: [...s] };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setSavedNotice(null);
    try {
      await callFn({
        action: 'update',
        token,
        prefs,
      });
      setSavedNotice('Preferences saved.');
    } catch (e: any) {
      setError(e.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              Link no longer valid
            </CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              If you continue to receive unwanted email, reply to one of our messages and we’ll
              remove you manually.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const seriesOptedOut = seriesId
    ? prefs.email_series_optouts.includes(seriesId)
    : false;

  return (
    <div className="min-h-screen bg-muted/30 py-12 px-4">
      <div className="mx-auto w-full max-w-2xl space-y-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            {workspaceName}
          </p>
          <h1 className="mt-1 text-2xl font-semibold">Email notifications</h1>
          <p className="text-sm text-muted-foreground">
            Preferences for <span className="font-medium text-foreground">{email}</span>
          </p>
        </div>

        {savedNotice && (
          <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
            <CheckCircle2 className="h-4 w-4 shrink-0 translate-y-0.5" />
            <span>{savedNotice}</span>
          </div>
        )}

        {seriesId && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">This meeting series</CardTitle>
              <CardDescription>
                Quick action: stop receiving messages about this specific meeting series only.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="series-optout">Stop emails for this meeting series</Label>
                <Switch
                  id="series-optout"
                  checked={seriesOptedOut}
                  onCheckedChange={(v) => {
                    setPrefs((p) => {
                      const s = new Set(p.email_series_optouts);
                      if (v) s.add(seriesId!);
                      else s.delete(seriesId!);
                      return { ...p, email_series_optouts: [...s] };
                    });
                  }}
                  disabled={saving}
                />
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">By message type</CardTitle>
            <CardDescription>Choose which kinds of email upstand.ai sends you.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="t-recap">Post-meeting recaps</Label>
              <Switch
                id="t-recap"
                checked={!prefs.email_type_optouts.includes('recap')}
                onCheckedChange={(v) => toggleType('recap', v)}
                disabled={saving || prefs.email_unsubscribed_all}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="t-pre">Pre-meeting nudges</Label>
              <Switch
                id="t-pre"
                checked={!prefs.email_type_optouts.includes('pre_nudge')}
                onCheckedChange={(v) => toggleType('pre_nudge', v)}
                disabled={saving || prefs.email_unsubscribed_all}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="t-brief">Morning briefing</Label>
              <Switch
                id="t-brief"
                checked={!prefs.email_type_optouts.includes('morning_briefing')}
                onCheckedChange={(v) => toggleType('morning_briefing', v)}
                disabled={saving || prefs.email_unsubscribed_all}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Unsubscribe from all</CardTitle>
            <CardDescription>
              Strongest opt-out. We will stop sending any email from upstand.ai.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <Label htmlFor="unsub-all">Unsubscribe from all emails</Label>
              <Switch
                id="unsub-all"
                checked={prefs.email_unsubscribed_all}
                onCheckedChange={(v) => setPrefs((p) => ({ ...p, email_unsubscribed_all: v }))}
                disabled={saving}
              />
            </div>
          </CardContent>
        </Card>

        <Separator />

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save preferences
          </Button>
        </div>
      </div>
    </div>
  );
}
